import { Mail } from "lucide-react";
import type { Resident } from "@shared/schema";

interface CommunityCardProps {
  resident: Resident;
  dataTestId?: string;
}

export function CommunityCard({ resident, dataTestId }: CommunityCardProps) {
  const initials = resident.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="h-full px-2">
      <div
        className="rounded-2xl bg-card neo-card transition-all duration-300 h-[180px] overflow-hidden flex flex-row"
        data-testid={dataTestId || `card-resident-${resident.id}`}
      >
        {/* Left Column: Full-height Image touching borders */}
        <div className="w-2/5 h-full flex-shrink-0 bg-gradient-to-br from-primary/10 via-secondary/10 to-accent/10 relative">
          {resident.photo ? (
            <img
              src={resident.photo}
              alt={resident.name}
              className="absolute inset-0 w-full h-full object-cover"
              data-testid={dataTestId ? `${dataTestId}-image` : `image-resident-${resident.id}`}
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center p-4">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-3">
                <span className="text-2xl font-bold text-foreground font-poppins">
                  {initials}
                </span>
              </div>
              <p className="text-xs text-muted-foreground font-inter">
                No photo
              </p>
            </div>
          )}
        </div>

        {/* Right Column: Information */}
        <div className="w-3/5 h-full p-5 flex flex-col justify-center flex-shrink-0">
          <div className="text-left">
            {/* Name */}
            <h3
              className="font-poppins text-xl font-bold text-foreground mb-1.5"
              data-testid={dataTestId ? `${dataTestId}-name` : `name-resident-${resident.id}`}
            >
              {resident.name}
            </h3>

            {/* Role */}
            <p
              className="font-inter text-sm text-primary font-semibold mb-2.5"
              data-testid={dataTestId ? `${dataTestId}-role` : `role-resident-${resident.id}`}
            >
              {resident.role}
            </p>

            {/* Department */}
            {resident.category && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-background/80 neo-card-sm mb-3.5">
                <span className="text-xs font-medium text-muted-foreground">Dept:</span>
                <span className="text-xs font-semibold text-foreground">{resident.category}</span>
              </div>
            )}

            {/* Contact Info */}
            {resident.email && (
              <div className="mt-3 pt-3 border-t border-border/50">
                <div
                  className="flex items-center gap-2 text-sm text-muted-foreground"
                  data-testid={dataTestId ? `${dataTestId}-email` : `email-resident-${resident.id}`}
                >
                  <Mail className="w-4 h-4 text-primary flex-shrink-0" />
                  <span className="font-inter truncate">{resident.email}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
